import { useEffect, useState } from "react";
import { format } from "date-fns";
import { ImageIcon, Pencil, Trash2 } from "lucide-react";
import ImageLightbox from "@/components/ImageLightbox";
import { toast } from "sonner";
import api, { apiError, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtIDR, fmtLocal, fmtNum, fmtRate } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import { PriceEditor, canEditPrice } from "@/components/PriceEditor";
import { LocationBadge, QtyProgress, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";

const BUFFER_TYPE_LABELS = {
  PURCHASE_SURPLUS: "Purchase surplus",
  ALLOCATED_TO_NEW_ORDER: "Allocated to new demand",
  AUTO_ORDER_ALLOCATION: "Auto-allocated to customer order",
  AUTO_ORDER_ALLOCATION_REVERSAL: "Buffer restored (order cancelled)",
  OWNER_ADJUSTMENT: "Owner adjustment",
};

export default function ProductSheet({ product, open, onOpenChange, onChanged, onEdit, onPurchase, canPurchase, canEdit, isOwner, locked }) {
  const { user } = useAuth();
  const showCosts = user?.role !== "viewer";
  const [purchases, setPurchases] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [adjQty, setAdjQty] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [adjMode, setAdjMode] = useState("add");
  const [adjusting, setAdjusting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [photoZoom, setPhotoZoom] = useState(false);

  useEffect(() => {
    setConfirmDelete(false);
    setAdjMode("add");
    setAdjQty("");
    setAdjReason("");
    if (product && open) {
      if (!showCosts) {
        setPurchases([]);
        setLedger([]);
        return;
      }
      setPurchases(null);
      setLedger(null);
      api
        .get(`/products/${product.id}/purchases`)
        .then((r) => setPurchases(r.data))
        .catch(() => setPurchases([]));
      api
        .get(`/products/${product.id}/buffer-ledger`)
        .then((r) => setLedger(r.data))
        .catch(() => setLedger([]));
    }
  }, [product, open, showCosts]);

  if (!product) return null;
  const img = productImage(product);

  const deleteProduct = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await api.delete(`/products/${product.id}`);
      toast.success("Product deleted");
      onOpenChange(false);
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deletePurchase = async (purchaseId) => {
    try {
      await api.delete(`/purchases/${purchaseId}`);
      toast.success("Purchase record removed");
      setPurchases((ps) => ps.filter((p) => p.id !== purchaseId));
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitAdjust = async () => {
    const qty = parseInt(adjQty, 10);
    if (!qty || qty <= 0 || !adjReason.trim()) return;
    if (adjMode === "reduce" && qty > product.buffer_qty) {
      toast.error(`Cannot reduce more than the available buffer (${product.buffer_qty})`);
      return;
    }
    setAdjusting(true);
    try {
      await api.post(`/products/${product.id}/buffer-adjustment`, {
        quantity: adjMode === "add" ? qty : -qty,
        reason: adjReason.trim(),
      });
      toast.success("Buffer adjusted");
      setAdjQty("");
      setAdjReason("");
      const { data } = await api.get(`/products/${product.id}/buffer-ledger`);
      setLedger(data);
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAdjusting(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="font-heading" data-testid="product-sheet-title">{product.name}</DrawerTitle>
        </DrawerHeader>
        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-4 pb-8">
          <div className="flex gap-4">
            {img ? (
              <button
                type="button"
                data-testid="product-sheet-photo"
                aria-label={`View photo of ${product.name}`}
                onClick={() => setPhotoZoom(true)}
                className="shrink-0"
              >
                <img src={img} alt={product.name} className="h-24 w-24 rounded-md border bg-stone-100 object-cover" />
              </button>
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-md border bg-stone-100">
                <ImageIcon className="h-8 w-8 text-stone-300" />
              </div>
            )}
            <div className="flex-1 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                <LocationBadge location={product.buying_location} />
                <StatusBadge status={product.status} />
              </div>
              <QtyProgress ordered={product.ordered_qty} purchased={product.purchased_qty} />
              {product.selling_price != null ? (
                <p className="text-sm text-muted-foreground" data-testid="sheet-selling-price">
                  Selling price: <span className="font-semibold text-stone-700">{fmtIDR(product.selling_price)}</span>
                </p>
              ) : showCosts && (
                <p className="text-sm font-medium text-amber-700" data-testid="sheet-price-missing">Selling price missing</p>
              )}
            </div>
          </div>

          {photoZoom && img && (
            <ImageLightbox src={img} alt={product.name} onClose={() => setPhotoZoom(false)} />
          )}

          {product.notes && (
            <div className="rounded-md border bg-accent/50 p-3 text-sm text-stone-700" data-testid="product-notes">
              {product.notes}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {canEditPrice(user?.role, product, locked) && (
              <PriceEditor key={`${product.id}:${product.selling_price}`} product={product} onSaved={onChanged} />
            )}
            {canPurchase && (
              <Button data-testid="sheet-record-purchase-btn" className="h-12 flex-1" onClick={() => onPurchase(product)}>
                Record Purchase
              </Button>
            )}
            {canEdit && !locked && (
              <Button variant="outline" data-testid="sheet-edit-btn" className="h-12" onClick={() => onEdit(product)}>
                <Pencil className="h-4 w-4" /> Edit
              </Button>
            )}
            {isOwner && !locked && (
              <Button
                variant={confirmDelete ? "destructive" : "outline"}
                data-testid="sheet-delete-btn"
                className="h-12"
                onClick={deleteProduct}
              >
                <Trash2 className="h-4 w-4" /> {confirmDelete ? "Confirm" : ""}
              </Button>
            )}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">PO Buffer</h3>
              <span data-testid="buffer-available" className="font-heading text-base font-extrabold text-emerald-600">
                {product.buffer_qty} available
              </span>
            </div>
            {isOwner && !locked && (
              <div className="mb-3 space-y-3 rounded-md border bg-accent/40 p-3">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Adjustment</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="buffer-mode-add"
                    onClick={() => setAdjMode("add")}
                    className={`flex h-11 items-center justify-center rounded-md border text-sm font-semibold transition-colors active:scale-[0.98] ${
                      adjMode === "add" ? "border-emerald-500 bg-emerald-500 text-white" : "bg-card text-stone-600 hover:bg-accent"
                    }`}
                  >
                    + Add Buffer
                  </button>
                  <button
                    type="button"
                    data-testid="buffer-mode-reduce"
                    onClick={() => setAdjMode("reduce")}
                    className={`flex h-11 items-center justify-center rounded-md border text-sm font-semibold transition-colors active:scale-[0.98] ${
                      adjMode === "reduce" ? "border-red-500 bg-red-500 text-white" : "bg-card text-stone-600 hover:bg-accent"
                    }`}
                  >
                    − Reduce Buffer
                  </button>
                </div>
                <div className="flex gap-2">
                  <div className="w-28 space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground" htmlFor="buffer-adjust-qty-input">Quantity</label>
                    <NumInput
                      id="buffer-adjust-qty-input"
                      data-testid="buffer-adjust-qty-input"
                      mode="int"
                      className="h-11"
                      placeholder="1"
                      value={adjQty}
                      onValue={setAdjQty}
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground" htmlFor="buffer-adjust-reason-input">Reason (required)</label>
                    <Input
                      id="buffer-adjust-reason-input"
                      data-testid="buffer-adjust-reason-input"
                      className="h-11"
                      placeholder="e.g. 1 unit damaged"
                      value={adjReason}
                      onChange={(e) => setAdjReason(e.target.value)}
                    />
                  </div>
                </div>
                <Button
                  data-testid="buffer-adjust-submit-button"
                  className="h-11 w-full"
                  onClick={submitAdjust}
                  disabled={adjusting || !adjQty || parseInt(adjQty, 10) <= 0 || !adjReason.trim()}
                >
                  {adjusting ? "Adjusting…" : adjMode === "add" ? "Adjust Buffer (+)" : "Adjust Buffer (−)"}
                </Button>
              </div>
            )}
            {showCosts && (ledger === null ? (
              <div className="grid place-items-center py-4">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : ledger.length === 0 ? (
              <p data-testid="buffer-history-empty" className="rounded-md border border-dashed py-4 text-center text-sm text-muted-foreground">
                No buffer movements yet
              </p>
            ) : (
              <div data-testid="buffer-history" className="space-y-2">
                {ledger.map((e) => {
                  const signed =
                    e.transaction_type === "OWNER_ADJUSTMENT"
                      ? e.quantity > 0
                        ? `+${e.quantity}`
                        : `${e.quantity}`
                      : e.transaction_type === "PURCHASE_SURPLUS" || e.transaction_type === "AUTO_ORDER_ALLOCATION_REVERSAL"
                        ? `+${e.quantity}`
                        : `-${e.quantity}`;
                  const color =
                    e.transaction_type === "PURCHASE_SURPLUS"
                      ? "text-emerald-600"
                      : ["ALLOCATED_TO_NEW_ORDER", "AUTO_ORDER_ALLOCATION"].includes(e.transaction_type)
                        ? "text-amber-600"
                        : e.quantity >= 0
                          ? "text-emerald-600"
                          : "text-red-600";
                  return (
                    <div key={e.id} data-testid={`buffer-entry-${e.id}`} className="rounded-md border bg-card p-3">
                      <p className="text-sm font-semibold">
                        {BUFFER_TYPE_LABELS[e.transaction_type] || e.transaction_type} ·{" "}
                        <span className={color}>{signed}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {e.created_by_name} · {format(new Date(e.created_at), "d MMM yyyy, HH:mm")}
                      </p>
                      {e.reason && <p className="text-xs text-muted-foreground">Reason: {e.reason}</p>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {showCosts && (
          <div>
            <h3 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Purchase History</h3>
            {purchases === null ? (
              <div className="grid place-items-center py-6">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : purchases.length === 0 ? (
              <p data-testid="purchase-history-empty" className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">
                No purchases recorded yet
              </p>
            ) : (
              <div data-testid="purchase-history" className="space-y-2">
                {purchases.map((p) => (
                  <div key={p.id} data-testid={`purchase-record-${p.id}`} className="flex items-center gap-3 rounded-md border bg-card p-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">
                        {p.quantity} ×{" "}
                        {p.buying_price != null
                          ? p.currency_symbol
                            ? fmtLocal(p.buying_price, p.currency_symbol)
                            : fmtNum(p.buying_price)
                          : "price n/a"}
                      </p>
                      {(p.planned_location || p.location) && (
                        <p className="text-xs text-muted-foreground" data-testid={`purchase-locations-${p.id}`}>
                          Planned: {p.planned_location || "UNASSIGNED"} · Bought at: {p.location || "not specified"}
                        </p>
                      )}
                      {p.converted_cost_idr != null && (
                        <p className="text-xs text-muted-foreground">
                          Cost: {fmtIDR(p.converted_cost_idr)} / unit
                          {p.margin_pct != null && ` · Margin ${p.margin_pct}%`}
                        </p>
                      )}
                      {p.exchange_rate_to_idr != null && p.currency_symbol && (
                        <p className="text-xs text-muted-foreground">Rate: {fmtRate(p.currency_symbol, p.exchange_rate_to_idr)}</p>
                      )}
                      {p.override_by_name && (
                        <p className="text-xs font-semibold text-red-600">Owner override: {p.override_by_name}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {p.purchased_by_name} · {format(new Date(p.purchased_at), "d MMM yyyy, HH:mm")}
                      </p>
                      {p.note && <p className="text-xs text-muted-foreground">{p.note}</p>}
                    </div>
                    {isOwner && (
                      <Button variant="ghost" size="icon" data-testid={`delete-purchase-${p.id}`} className="h-9 w-9 text-stone-400" onClick={() => deletePurchase(p.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
